import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';
import { builtEnvironment } from '@shared/builtEnvironment';
import { isRunningLocal } from '@shared/isRunningEnvironment';

interface UseIntakeFormActionsResult {
  navigateToUpdateIntakeForm: (clientId: string) => Promise<void>;
}

export function useIntakeFormActions(): UseIntakeFormActionsResult {
  const { getAccessTokenSilently } = useAuth0();

  const navigateToUpdateIntakeForm = useCallback(
    async (clientId: string) => {
      try {
        const token = await getAccessTokenSilently();
        const apiBase = `/${isRunningLocal() ? 'dev' : builtEnvironment}/api`;

        await fetch(`${apiBase}/reopen-form/${clientId}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const response = await fetch(`${apiBase}/client/${clientId}/form-reference`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage =
            errorData?.errorMessage ?? 'Failed to load intake form. Please try again.';
          toast.error('Failed to load intake form', {
            description: errorMessage,
          });
          return;
        }

        const { formReferenceId } = await response.json();
        const baseUrl = window.location.origin;
        const formUrl = `${baseUrl}/onboarding-form/${formReferenceId}`;
        window.open(formUrl, '_blank', 'noopener,noreferrer');
      } catch (err) {
        const errorMessage =
          'An error occurred while loading the intake form. Please try again.';
        toast.error('Failed to load intake form', {
          description: errorMessage,
        });
      }
    },
    [getAccessTokenSilently],
  );

  return {
    navigateToUpdateIntakeForm,
  };
}
